import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAIPreferences } from '@/contexts/AIPreferencesContext';
import type { SavedLesson } from '@/mocks/lessons';
import Pressable from '@/components/base/Pressable';

interface AISummaryProps {
  lesson: SavedLesson;
}

export default function AISummary({ lesson }: AISummaryProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const { preferences } = useAIPreferences();

  const handleCopy = () => {
    navigator.clipboard.writeText(lesson.aiSummary).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Show fewer lines in "Simple" mode by default
  const lineClampClass = preferences.explanationStyle === 'Simple' ? 'line-clamp-2' : 'line-clamp-3';

  const t = {
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textSecondary: isDark ? 'text-white/50' : 'text-black/50',
    textMuted: isDark ? 'text-white/60' : 'text-black/60',
    textFaint: isDark ? 'text-white/25' : 'text-black/25',
    iconBg: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]',
    iconMuted: isDark ? 'text-white/50' : 'text-black/40',
    cardBg: isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]',
    cardBorder: isDark ? 'border-white/[0.05]' : 'border-black/[0.05]',
    cardDivider: isDark ? 'border-white/[0.04]' : 'border-black/[0.04]',
    conceptTag: isDark ? 'bg-white/[0.04] border-white/[0.06] text-white/50' : 'bg-black/[0.04] border-black/[0.06] text-black/50',
    copyText: isDark ? 'text-white/30' : 'text-black/30',
    copyHover: isDark ? 'hover:text-white/60' : 'hover:text-black/60',
    emerald: isDark ? 'text-emerald-400' : 'text-emerald-600',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3, ease: [0.32, 0.72, 0, 1] }}
      className="px-5 pb-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg ${t.iconBg} flex items-center justify-center`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-sparkling-line ${t.iconMuted} text-sm`} />
          </div>
        </div>
        <div>
          <h3 className={`${t.textPrimary} text-sm font-semibold`}>AI Summary</h3>
          <p className={`${t.textFaint} text-[11px]`}>{preferences.explanationStyle} style</p>
        </div>
      </div>

      <div className={`rounded-2xl ${t.cardBg} border ${t.cardBorder} p-4`}>
        <p className={`${t.textMuted} text-xs leading-relaxed ${expanded ? '' : lineClampClass}`}>
          {lesson.aiSummary}
        </p>

        <div className={`flex items-center justify-between mt-3 pt-3 border-t ${t.cardDivider}`}>
          <button
            onClick={() => setExpanded(!expanded)}
            className={`${t.copyText} ${t.copyHover} text-[11px] font-semibold transition-colors cursor-pointer`}
          >
            {expanded ? 'Show less' : 'Read more'}
          </button>

          <Pressable onClick={handleCopy} scale={0.92}>
            <div className={`flex items-center gap-1.5 ${t.copyText} text-[11px] font-medium`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <AnimatePresence mode="wait">
                  {copied ? (
                    <motion.i key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} className={`ri-check-line ${t.emerald}`} />
                  ) : (
                    <motion.i key="copy" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} className="ri-file-copy-line" />
                  )}
                </AnimatePresence>
              </div>
              {copied ? 'Copied!' : 'Copy'}
            </div>
          </Pressable>
        </div>
      </div>

      {/* Key Concepts */}
      <div className="mt-4">
        <h4 className={`${t.textSecondary} text-[11px] font-semibold uppercase tracking-wider mb-2`}>Key Concepts</h4>
        <div className="flex flex-wrap gap-1.5">
          {lesson.keyConcepts.map((concept, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 + i * 0.06 }}
              className={`px-3 py-1.5 rounded-full border ${t.conceptTag} text-[11px] font-medium`}
            >
              {concept}
            </motion.span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}